class AccordionItem extends HTMLElement {
      constructor() {
        super();
        this._isInitialized = false;
      }

      static get observedAttributes() {
        return [
          'heading',
          'icon',
          'background-color',
          'foreground-color',
          'border-color',
          'border-thickness',
          'border-radius',
          'padding-top',
          'padding-bottom'
        ];
      }

      connectedCallback() {
        if (!this._isInitialized) {
          this.render();
          this.setupEventListeners();
          this._isInitialized = true;
        }
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue && this._isInitialized) {
          this.updateStyles();
        }
      }

      get heading() {
        return this.getAttribute('heading') || 'Accordion Heading';
      }

      get icon() {
        return this.getAttribute('icon') || 'plus';
      }

      get backgroundColor() {
        return this.getAttribute('background-color') || '';
      }

      get foregroundColor() {
        return this.getAttribute('foreground-color') || '';
      }

      get borderColor() {
        return this.getAttribute('border-color') || '';
      }

      get borderThickness() {
        return this.getAttribute('border-thickness') || '';
      }

      get borderRadius() {
        return this.getAttribute('border-radius') || '';
      }

      get paddingTop() {
        return this.getAttribute('padding-top') || '0';
      }

      get paddingBottom() {
        return this.getAttribute('padding-bottom') || '0';
      }

      render() {
        let originalContent = this.innerHTML;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = originalContent;

        const isMeaningful = (str) => str.trim() !== '';

        // --- 1. Handle product__description ---
        const descriptionElements = tempDiv.querySelectorAll('.product__description.rte.quick-add-hidden');
        let hasMeaningfulDescription = false;
        for (const el of descriptionElements) {
          const clone = el.cloneNode(true);
          clone.querySelectorAll('br').forEach(br => br.remove());
          if (isMeaningful(clone.textContent)) {
            hasMeaningfulDescription = true;
            break;
          }
        }

        if (hasMeaningfulDescription) {
          const firstP = tempDiv.querySelector('p');
          if (firstP && !firstP.closest('.product__description')) {
            firstP.remove();
          }
        }

        // --- 2. Handle metafield-multi_line_text_field ---
        const metafieldSpans = tempDiv.querySelectorAll('span.metafield-multi_line_text_field');
        for (const span of metafieldSpans) {
          if (isMeaningful(span.textContent)) {
            const parentP = span.closest('p');
            if (parentP) {
              let prev = parentP.previousElementSibling;
              while (prev && prev.tagName !== 'P') {
                prev = prev.previousElementSibling;
              }
              if (prev) {
                prev.remove();
              }
            }
          }
        }

        originalContent = tempDiv.innerHTML;

        const iconSVG = this.icon === 'caret'
          ? `<svg class="accordion-item-icon icon-caret" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path>
            </svg>`
          : `<svg class="accordion-item-icon icon-plus" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line class="horizontal" x1="4" y1="12" x2="20" y2="12" />
              <line class="vertical" x1="12" y1="4" x2="12" y2="20" />
            </svg>`;

        this.innerHTML = `
          <style>
            accordion-item {
              display: block;
              padding-top: ${this.paddingTop}px;
              padding-bottom: ${this.paddingBottom}px;
            }

            accordion-item .accordion-item {
              width: 100%;
              max-width: 100%;
              position: relative;
              box-sizing: border-box;
            }

            accordion-item .accordion-item-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 0.75rem 1rem;
              cursor: pointer;
              transition: background-color 0.2s ease;
              user-select: none;
              box-sizing: border-box;
            }

            accordion-item .accordion-item-header-content {
              display: flex;
              align-items: center;
              gap: 0.5rem;
              flex: 1;
            }

            accordion-item .accordion-item-header-content p {
              font-size: 0.875rem;
              line-height: 1.25rem;
              margin: 0;
              font-weight: 500;
            }

            accordion-item .accordion-item-content {
              font-size: 0.875rem;
              line-height: 1.25rem;
              max-width: 100%;
              overflow: hidden;
              transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              max-height: 0;
              box-sizing: border-box;
            }

            accordion-item .accordion-item-inner {
              padding-inline: 1rem;
              padding-bottom: 0.75rem;
              box-sizing: border-box;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }

            accordion-item .accordion-item-icon {
              width: 1.25rem;
              height: 1.25rem;
              flex-shrink: 0;
            }

            accordion-item .accordion-item-icon.icon-caret {
              transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              transform-origin: center;
            }

            accordion-item .accordion-item-icon.icon-caret.rotate {
              transform: rotate(180deg);
            }

            accordion-item .accordion-item-icon.icon-plus line {
              transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              transform-origin: 12px 12px;
            }

            accordion-item .accordion-item-icon.icon-plus.rotate .vertical {
              opacity: 0;
              transform: rotate(90deg);
            }

            accordion-item .accordion-item-icon.icon-plus.rotate .horizontal {
              transform: rotate(0deg);
            }
          </style>
          <div class="accordion-item">
            <div class="accordion-item-header">
              <div class="accordion-item-header-content">
                <p>${this.heading}</p>
              </div>
              ${iconSVG}
            </div>
            <div class="accordion-item-content">
              <div class="accordion-item-inner">
                ${originalContent}
              </div>
            </div>
          </div>
        `;

        this.updateStyles();
      }

      updateStyles() {
        const accordion = this.querySelector('.accordion-item');
        if (!accordion) return;

        if (this.backgroundColor) {
          accordion.style.backgroundColor = this.backgroundColor;
        }
        if (this.foregroundColor) {
          accordion.style.color = this.foregroundColor;
        }
        if (this.borderColor) {
          accordion.style.borderBottomColor = this.borderColor;
        }
        if (this.borderThickness) {
          accordion.style.borderBottomWidth = this.borderThickness + 'px';
          accordion.style.borderBottomStyle = 'solid';
        }
        if (this.borderRadius) {
          accordion.style.borderRadius = this.borderRadius + 'px';
        }
      }

      updateHeight() {
        const content = this.querySelector('.accordion-item-content');
        const inner = this.querySelector('.accordion-item-inner');
        
        if (content && inner && content.classList.contains('open')) {
          // Use scrollHeight to get actual content height including nested accordions
          content.style.maxHeight = inner.scrollHeight + 'px';
        }
      }

      updateParentHeights() {
        // Traverse up and update all parent accordions
        let parent = this.parentElement;
        while (parent) {
          if (parent.tagName === 'ACCORDION-ITEM') {
            parent.updateHeight();
          }
          parent = parent.parentElement;
        }
      }

      setupEventListeners() {
        const header = this.querySelector('.accordion-item-header');
        const content = this.querySelector('.accordion-item-content');
        const inner = this.querySelector('.accordion-item-inner');
        const icon = this.querySelector('.accordion-item-icon');

        if (!header || !content || !inner) return;

        header.addEventListener('click', () => {
          if (content.classList.contains('open')) {
            content.style.maxHeight = '0px';
            content.classList.remove('open');
            if (icon) icon.classList.remove('rotate');
            this.dispatchEvent(new CustomEvent('accordion-close', { bubbles: true, detail: { source: this } }));
          } else {
            content.classList.add('open');
            const height = inner.scrollHeight;
            content.style.maxHeight = height + 'px';
            if (icon) icon.classList.add('rotate');
            this.dispatchEvent(new CustomEvent('accordion-open', { bubbles: true, detail: { source: this } }));
            
            // Recalculate after transition
            setTimeout(() => {
              if (content.classList.contains('open')) {
                this.updateHeight();
                this.updateParentHeights();
              }
            }, 350);
          }
        });

        // Listen for nested accordion open/close events
        this.addEventListener('accordion-open', (e) => {
          if (e.detail.source !== this) {
            e.stopPropagation();
            setTimeout(() => {
              this.updateHeight();
              this.updateParentHeights();
            }, 50);
          }
        });

        this.addEventListener('accordion-close', (e) => {
          if (e.detail.source !== this) {
            e.stopPropagation();
            setTimeout(() => {
              this.updateHeight();
              this.updateParentHeights();
            }, 50);
          }
        });
      }

      // Public API
      open() {
        const content = this.querySelector('.accordion-item-content');
        const inner = this.querySelector('.accordion-item-inner');
        const icon = this.querySelector('.accordion-item-icon');

        if (content && inner && !content.classList.contains('open')) {
          content.classList.add('open');
          content.style.maxHeight = inner.scrollHeight + 'px';
          if (icon) icon.classList.add('rotate');
          this.dispatchEvent(new CustomEvent('accordion-open', { bubbles: true, detail: { source: this } }));
        }
      }

      close() {
        const content = this.querySelector('.accordion-item-content');
        const icon = this.querySelector('.accordion-item-icon');

        if (content && content.classList.contains('open')) {
          content.style.maxHeight = '0px';
          content.classList.remove('open');
          if (icon) icon.classList.remove('rotate');
          this.dispatchEvent(new CustomEvent('accordion-close', { bubbles: true, detail: { source: this } }));
        }
      }

      toggle() {
        const content = this.querySelector('.accordion-item-content');
        if (content && content.classList.contains('open')) {
          this.close();
        } else {
          this.open();
        }
      }

      isOpen() {
        const content = this.querySelector('.accordion-item-content');
        return content ? content.classList.contains('open') : false;
      }
    }

    customElements.define('accordion-item', AccordionItem);